import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { EnvelopeIcon, CheckCircleIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../../hooks/useAuth';
import { useToast } from '../../hooks/useToast';
import LoadingSpinner from '../../components/LoadingSpinner';
import MobileButton from '../../components/MobileButton';
import Logo from '../../components/Logo';

const EmailConfirmationPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { resetPassword } = useAuth();
  const { showToast } = useToast();
  const [isResending, setIsResending] = useState(false);

  const email = searchParams.get('email') || '';

  const handleResendEmail = async () => {
    if (!email) {
      showToast('error', 'No email provided', 'Please go back and sign up again');
      return;
    }

    setIsResending(true);
    try {
      // Use the reset password function to resend email confirmation
      // This is a workaround since Supabase doesn't have a direct resend confirmation function
      if (resetPassword) {
        await resetPassword(email);
        showToast('success', 'Email sent!', 'Please check your inbox for the confirmation link');
      } else {
        showToast('error', 'Reset password function not available');
      }
    } catch (error) {
      showToast('error', 'Failed to resend email', error instanceof Error ? error.message : 'Unknown error');
    } finally {
      setIsResending(false);
    }
  };

  const handleGoToSignIn = () => {
    navigate('/signin');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-indigo-900 relative overflow-hidden flex flex-col justify-center py-12 px-4 sm:px-6 lg:px-8">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_80%,rgba(120,119,198,0.3),transparent_50%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_80%_20%,rgba(255,255,255,0.1),transparent_50%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_40%_40%,rgba(120,119,198,0.2),transparent_50%)]" />
      </div>

      <div className="relative z-10">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <div className="text-center">
            <div className="mx-auto flex items-center justify-center mb-6">
              <Logo size="lg" showText={false} />
            </div>
            <h2 className="text-3xl font-bold text-white mb-2">
              Check Your Email
            </h2>
            <p className="text-lg text-white/70">
              We've sent you a confirmation link
            </p>
          </div>
        </div>

        <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
          <div className="bg-white/10 backdrop-blur-lg py-8 px-4 shadow-2xl rounded-2xl border border-white/20 sm:px-10">
            
            {/* Email Icon */}
            <div className="flex justify-center mb-6">
              <div className="bg-blue-500/20 p-4 rounded-full">
                <EnvelopeIcon className="h-12 w-12 text-blue-400" />
              </div>
            </div>

            {/* Main Message */}
            <div className="text-center mb-8">
              <h3 className="text-xl font-semibold text-white mb-4">
                Almost there!
              </h3>
              <p className="text-white/80 mb-4">
                We've sent a confirmation email to:
              </p>
              <p className="text-blue-300 font-medium text-lg bg-white/10 px-4 py-2 rounded-lg border border-white/20">
                {email || 'your email address'}
              </p>
            </div>

            {/* Instructions */}
            <div className="mb-8">
              <h4 className="text-lg font-medium text-white mb-3">
                What to do next:
              </h4>
              <div className="space-y-3">
                <div className="flex items-start space-x-3">
                  <div className="bg-blue-500/20 p-1 rounded-full mt-0.5">
                    <div className="w-2 h-2 bg-blue-400 rounded-full"></div>
                  </div>
                  <p className="text-white/80 text-sm">
                    Check your inbox for an email from Thriftalyze
                  </p>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="bg-blue-500/20 p-1 rounded-full mt-0.5">
                    <div className="w-2 h-2 bg-blue-400 rounded-full"></div>
                  </div>
                  <p className="text-white/80 text-sm">
                    Click the "Confirm Your Email" button in the email
                  </p>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="bg-blue-500/20 p-1 rounded-full mt-0.5">
                    <div className="w-2 h-2 bg-blue-400 rounded-full"></div>
                  </div>
                  <p className="text-white/80 text-sm">
                    You'll be redirected back to sign in
                  </p>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="space-y-4">
              <MobileButton
                onClick={handleGoToSignIn}
                variant="primary"
                className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-semibold py-3 px-4 rounded-xl shadow-lg transform transition-all duration-200 hover:scale-105"
              >
                <CheckCircleIcon className="h-5 w-5 mr-2" />
                Go to Sign In
              </MobileButton>

              <MobileButton
                onClick={handleResendEmail}
                disabled={isResending || !email}
                variant="secondary"
                className="w-full bg-white/10 hover:bg-white/20 text-white font-medium py-3 px-4 rounded-xl border border-white/20 transition-all duration-200 disabled:opacity-50"
              >
                {isResending ? (
                  <>
                    <LoadingSpinner size="sm" className="mr-2" />
                    Resending...
                  </>
                ) : (
                  <>
                    <ArrowPathIcon className="h-5 w-5 mr-2" />
                    Resend Email
                  </>
                )}
              </MobileButton>
            </div>

            {/* Additional Info */}
            <div className="mt-8 pt-6 border-t border-white/20">
              <div className="text-center">
                <p className="text-white/60 text-sm mb-2">
                  Didn't receive the email?
                </p>
                <div className="text-white/60 text-xs space-y-1">
                  <p>• Check your spam/junk folder</p>
                  <p>• Make sure you entered the correct email</p>
                  <p>• The email may take a few minutes to arrive</p>
                </div>
              </div>
            </div>

            {/* Help Link */}
            <div className="mt-6 text-center">
              <p className="text-white/60 text-sm">
                Need help?{' '}
                <button
                  onClick={() => showToast('info', 'Contact Support', 'Please email support@snapalyze.org for assistance')}
                  className="text-blue-300 hover:text-blue-200 font-medium"
                >
                  Contact Support
                </button>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailConfirmationPage;
